import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from '@/composables';
import { getCustomConversionsApiRequest } from '@/services/api/custom-conversions-api';
import { populateAffiliateNetworksStoreCacheApiRequest } from '@/services/api/api-services/affiliate-networks';
import { useStore } from '@/store';

export type TrackConversionsNoClickIdsDialogData = {
  campaign: Record<string, unknown> & {
    id: string;
    destinationType?: string;
    flow?: { id: string; nameFull?: string };
    flowInline?: { rules: Array<{ paths: Array<{ offers: Offer[]; landings: Landing[]; directLinking?: boolean }> }> };
  };
  linkType?: string;
};

type TrackConversionsForm = {
  trackingDomainId: string;
  offer: Offer | null;
  landing: Landing | null;
  payout: string | null;
  txid: string | null;
  status: string | null;
  eid: string | null;
  trackCustomConversions: { values: string[]; name?: string };
  trackCustomConversionsValue: string;
  customs: string[];
};

const defaultForm = (trackingDomainId: string): TrackConversionsForm => ({
  trackingDomainId,
  offer: null,
  landing: null,
  payout: null,
  txid: null,
  status: null,
  eid: null,
  trackCustomConversions: {
    values: [],
  },
  trackCustomConversionsValue: '',
  customs: [''],
});

type FlowDataSource = {
  rules: Array<{
    paths: Array<{
      offers: Offer[];
      landings: Landing[];
      directLinking?: boolean;
    }>;
  }>;
};

export const useCampaignTrackConversionsNoClickIds = (
  getData: () => TrackConversionsNoClickIdsDialogData,
  emitClose: () => void,
) => {
  const { t } = useI18n();
  const store = useStore();
  const landingItems = ref<Landing[]>([]);
  const offerItems = ref<Offer[]>([]);
  const trackCustomConversionsSwitcher = ref(false);
  const customConversionsItems = ref<Array<{ values: string[]; status?: string }>>([]);
  const selectedLinkMode = ref(getData().linkType || 'postbackUrl');

  const form = ref<TrackConversionsForm>(defaultForm(store.user.trackingDomainId));

  const campaign = computed(() => getData().campaign);

  const linkModes = computed(() => [
    { id: 'postbackUrl', name: t('Postback URL') },
    { id: 'conversionPixelUrl', name: t('Conversion Pixel URL') },
    { id: 'conversionPixel', name: t('Conversion Pixel') },
    { id: 'conversionScript', name: t('Conversion Script') },
    { id: 'postbackUrlTxt', name: t('Postback URL (for TXT response)') },
  ]);

  const affiliateNetworkOfSelectedOffer = computed(
    () =>
      store.affiliateNetworksObject?.[form.value.offer?.affiliateNetworkId]
        ?.affiliateNetworkTemplate || {},
  );

  const finalPostbackUrl = computed(() => {
    const trackingDomain = store.trackingDomainsObject[form.value.trackingDomainId];
    if (!trackingDomain) {
      return '';
    }

    const domain = `${trackingDomain.httpsAvailable ? 'https' : 'http'}://${trackingDomain.domain}`;
    const searchParams = [`caid=${campaign.value.id}`];

    if (campaign.value.destinationType === 'flow' && campaign.value.flow) {
      searchParams.push(`flid=${campaign.value.flow.id}`);
    }

    if (form.value.landing) {
      searchParams.push(`lpid=${form.value.landing.id}`);
    }

    if (form.value.offer) {
      searchParams.push(`oid=${form.value.offer.id}`);
    }

    if (form.value.eid) {
      searchParams.push(`eid=${form.value.eid}`);
    }

    searchParams.push(`payout=${form.value.payout || 'OPTIONAL'}`);
    searchParams.push(`txid=${form.value.txid || 'OPTIONAL'}`);

    if (!trackCustomConversionsSwitcher.value) {
      searchParams.push(`status=${form.value.status || 'OPTIONAL'}`);
    }

    if (trackCustomConversionsSwitcher.value) {
      searchParams.push(`cet=${form.value.trackCustomConversionsValue}`);
    }

    form.value.customs.forEach((item, index) => {
      if (item) {
        searchParams.push(`custom${index + 1}=${item}`);
      }
    });

    const searchParamsString = searchParams.join('&');

    switch (selectedLinkMode.value) {
      case 'postbackUrl':
        return `${domain}/postback?attribution=cid-caid&${searchParamsString}`;
      case 'conversionPixelUrl':
        return `${domain}/conversion.gif?attribution=cid-caid&${searchParamsString}`;
      case 'conversionPixel':
        return `<img src="${domain}/conversion.gif?attribution=cid-caid&${searchParamsString}" width="1" height="1" border="0" alt="" />`;
      case 'conversionScript':
        return `<script async type="text/javascript" src="${domain}/conversion.js?attribution=cid-caid&${searchParamsString}"></sc` + `ript>`;
      case 'postbackUrlTxt':
        return `${domain}/conversion.txt?attribution=cid-caid&${searchParamsString}`;
      default:
        return '';
    }
  });

  const trackCustomConversionsChange = () => {
    form.value.trackCustomConversionsValue = form.value.trackCustomConversions.values[0];
  };

  const deleteCustomRow = () => {
    form.value.customs.pop();
  };

  const addCustomRow = () => {
    form.value.customs.push('');
  };

  const close = () => {
    emitClose();
  };

  const calculateOffersAndLandings = () => {
    if (campaign.value.destinationType === 'url') {
      return;
    }

    let dataSource: FlowDataSource | undefined;

    if (campaign.value.destinationType === 'flow' && campaign.value.flow) {
      dataSource = campaign.value.flow as FlowDataSource;
    }

    if (campaign.value.destinationType === 'flowInline' && campaign.value.flowInline) {
      dataSource = campaign.value.flowInline;
    }

    if (!dataSource?.rules) {
      return;
    }

    let offersWithDuplicates: Offer[] = [];
    let landingsWithDuplicates: Landing[] = [];

    dataSource.rules.forEach((rule) => {
      rule.paths.forEach((path) => {
        offersWithDuplicates = offersWithDuplicates.concat(path.offers);
        if (!path.directLinking) {
          landingsWithDuplicates = landingsWithDuplicates.concat(path.landings);
        }
      });
    });

    const offersWithDuplicatesStr = offersWithDuplicates.map((item) => JSON.stringify(item));
    const landingsWithDuplicatesStr = landingsWithDuplicates.map((item) => JSON.stringify(item));
    const offerUniqueSet = new Set(offersWithDuplicatesStr);
    const landingsUniqueSet = new Set(landingsWithDuplicatesStr);

    offerItems.value = Array.from(offerUniqueSet).map((item) => JSON.parse(item));
    landingItems.value = Array.from(landingsUniqueSet).map((item) => JSON.parse(item));

    form.value.offer = offerItems.value[0] || null;
    form.value.landing = landingItems.value[0] || null;
  };

  watch(
    () => form.value.offer,
    () => {
      if (affiliateNetworkOfSelectedOffer.value) {
        form.value.payout = affiliateNetworkOfSelectedOffer.value.payoutPlaceholder;
        form.value.txid = affiliateNetworkOfSelectedOffer.value.transactionIdPlaceholder;
        form.value.status = affiliateNetworkOfSelectedOffer.value.statusPlaceholder;
      } else {
        form.value.payout = '';
        form.value.txid = '';
        form.value.status = '';
      }
    },
    { immediate: true },
  );

  onMounted(async () => {
    calculateOffersAndLandings();

    const customConversions = await getCustomConversionsApiRequest({ status: 'active' });
    customConversionsItems.value = customConversions.filter(
      (item: { status?: string }) => item.status === 'active',
    );

    if (customConversionsItems.value[0]) {
      form.value.trackCustomConversions = customConversionsItems.value[0];
      form.value.trackCustomConversionsValue = form.value.trackCustomConversions.values[0];
    }

    if (!store.cache.affiliateNetworks.isUploaded) {
      await populateAffiliateNetworksStoreCacheApiRequest();
    }
  });

  return {
    t,
    store,

    campaign,
    form,
    landingItems,
    offerItems,
    trackCustomConversionsSwitcher,
    customConversionsItems,
    selectedLinkMode,
    linkModes,
    affiliateNetworkOfSelectedOffer,
    finalPostbackUrl,
    trackCustomConversionsChange,
    deleteCustomRow,
    addCustomRow,
    close,
  };
};
